import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";

import { updateFieldSchema } from "../../reducers/form/schema";
import { useForm } from "./withFormStore";
import { updateFieldErrors, updateFieldErrorsDisplay } from "../../reducers/form/errors";
import { useFieldContext } from "./Field";
import invariant from "invariant";

// This hook is used by the `<Field>` component to retrieve form context and redux stored
// field errors state.
//
export const useFieldErrors = inputFieldName => {
  const { formKey } = useForm();

  // Allow using the hook without a Field component child by passing an explicit
  // `fieldName` when needed.
  const fieldContext = useFieldContext() || {};
  const fieldName = inputFieldName || fieldContext.fieldName;

  invariant(
    fieldName,
    `A field name is required by the useFieldErrors hook, please ensure that you call it 
    within a Field component child or you pass it an explicit \`fieldName\` argument.`
  );

  const schemaSelector = ({ forms }) => forms[formKey]?.schema[fieldName] || {};
  const errorDataSelector = ({ forms }) => forms[formKey]?.errors[fieldName] || {};

  // The isEqual function performs a deep comparison of the selected values,
  // ensuring that the component only re-renders when there are actual changes
  // in the content of schema or errorData, not just changes in object references.
  const schema = useSelector(schemaSelector, isEqual);
  const errorData = useSelector(errorDataSelector, isEqual);

  const errors = errorData.errors || [];
  const showErrors = errorData.show || false;
  const isInvalid = errors.length > 0 && showErrors;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateFieldSchema(formKey, fieldName, schema));
  }, [dispatch, fieldName, formKey, schema]);

  const addErrors = useCallback(
    newErrors => {
      dispatch(updateFieldErrors(formKey, fieldName, newErrors));

      if (!showErrors) {
        dispatch(updateFieldErrorsDisplay(formKey, fieldName, true));
      }
    },
    [dispatch, fieldName, formKey, showErrors]
  );

  const removeErrors = useCallback(() => {
    dispatch(updateFieldErrors(formKey, fieldName, []));
    if (showErrors) {
      dispatch(updateFieldErrorsDisplay(formKey, fieldName, false));
    }
  }, [dispatch, fieldName, formKey, showErrors]);

  return {
    isInvalid,
    errors,
    showErrors,
    addErrors,
    removeErrors
  };
};
